/**
 * Project: Aniatek
 * Author: Carlos A. Loaiza | Connect & Launch
 * Tel: +52 (667) 247 - 6316
 * Intellectual Property: All rights reserved (c) 2026
 */

<template>
  <div class="paso-uno-container">
    <div class="form-section-header">
      <h3 class="header-icon">Paso 1: Datos de Contacto</h3>
      <p>Ingresa la información personal del nuevo prospecto o selecciona uno existente.</p>
      <p class="leyenda-obligatoria">
        <span class="asterisco">*</span> Los campos marcados son obligatorios.
      </p>
    </div>

    <div class="form-grid">

      <div class="form-group span-3">
        <label for="search-contacto">Buscar y seleccionar contacto existente</label>
        <div class="search-container">
          <input id="search-contacto" type="text" v-model="searchQuery"
            placeholder="Escribe el nombre o haz click para ver la lista completa." @focus="showResults = true"
            @blur="hideResultsDelayed" :readonly="!!formData.contactoExistente" class="input-search"
            :class="{ 'contact-loaded': !!formData.contactoExistente }">

          <span v-if="formData.contactoExistente" class="clear-selection-button"
            @mousedown.prevent="seleccionarNuevoContacto()"
            :title="`Borrar selección de ${contactoSeleccionadoNombre}`">
            ✖️
          </span>

          <ul v-if="showResults && contactosFiltrados.length" class="autocomplete-results">
            <li @mousedown="seleccionarNuevoContacto()">
              -- Crear un contacto nuevo --
            </li>
            <li v-for="contacto in contactosFiltrados" :key="contacto.id_cliente"
              @mousedown="seleccionarContacto(contacto)">
              {{ contacto.nombreCompleto }}
            </li>
          </ul>

          <div v-else-if="showResults && searchQuery && contactosFiltrados.length === 0" class="no-results-message">
            No se encontraron coincidencias.
          </div>
        </div>
      </div>
      <div class="form-group">
        <label for="nombre">Nombre*</label>
        <input id="nombre" type="text" :value="formData.nombre"
          @input="updateField('nombre', $event.target.value, 'texto')" required placeholder="Nombre">
      </div>
      <div class="form-group">
        <label for="apellidoPaterno">Apellido paterno*</label>
        <input id="apellidoPaterno" type="text" :value="formData.apellidoPaterno"
          @input="updateField('apellidoPaterno', $event.target.value, 'texto')" placeholder="Apellido paterno">
      </div>
      <div class="form-group">
        <label for="apellidoMaterno">Apellido materno*</label>
        <input id="apellidoMaterno" type="text" :value="formData.apellidoMaterno"
          @input="updateField('apellidoMaterno', $event.target.value, 'texto')" placeholder="Apellido materno">
      </div>
      <div class="form-group">
        <label for="telefono">Teléfono*</label>
        <input id="telefono" type="tel" :value="formData.telefono"
          @input="updateField('telefono', $event.target.value, 'telefono')" maxlength="10" required
          placeholder="Telefono">
      </div>
      <div class="form-group">
        <label for="celular">Celular*</label>
        <input id="celular" type="text" :value="formData.celular"
          @input="updateField('celular', $event.target.value, 'telefono')" maxlength="10" placeholder="Celular">
      </div>
      <div class="form-group">
        <label for="email">Correo electrónico*</label>
        <input id="email" type="email" :value="formData.email" @input="updateField('email', $event.target.value)"
          placeholder="Correo electronico">
      </div>

      <div class="form-group">
        <label for="estado">Estado*</label>
        <select id="estado" :value="formData.estado" @change="onCambioEstado($event.target.value)" required>
          <option disabled value="">-- Elige un estado --</option>
          <option v-for="estadoObj in estadosData.estados" :key="estadoObj.nombre" :value="estadoObj.nombre">
            {{ estadoObj.nombre }}
          </option>
        </select>
      </div>

      <div class="form-group">
        <label for="localidad">Localidad / Municipio*</label>
        <select id="localidad" :value="formData.localidad" @change="updateField('localidad', $event.target.value)"
          required :disabled="!formData.estado">
          <option disabled value="">-- {{ formData.estado ? 'Elige un municipio' : 'Primero selecciona un estado' }} --
          </option>
          <option v-for="municipio in municipiosDisponibles" :key="municipio" :value="municipio">
            {{ municipio }}
          </option>
        </select>
      </div>

      <div class="form-group">
        <label for="fuente">Fuente de contacto*</label>
        <select id="fuente" :value="formData.fuente" @change="updateField('fuente', $event.target.value)" required>
          <option disabled value="">-- Elige una fuente --</option>
          <option v-for="fuente in listas.fuentes" :key="fuente" :value="fuente">{{ fuente }}</option>
        </select>
      </div>

      <div class="form-group span-3">
        <label for="notas">Notas*</label>
        <textarea id="notas" :value="formData.notas" @input="updateField('notas', $event.target.value)" rows="4"
          placeholder="Escribe anotaciones relevantes sobre el cliente o el proyecto..."></textarea>
      </div>
    </div>
  </div>
</template>

<script>
import clientService from '@/services/clientService.js';
import dataEstadosMunicipios from '@/data/estados_municipios.json';

export default {
  props: {
    formData: { type: Object, required: true },
    listas: { type: Object, required: true },
  },
  data() {
    return {
      contactos: [],
      estadosData: dataEstadosMunicipios,
      searchQuery: '',     
      showResults: false,  
    }
  },
  computed: {
    municipiosDisponibles() {
      if (!this.formData.estado) return [];

      const estadoEncontrado = this.estadosData.estados.find(
        e => e.nombre === this.formData.estado
      );

      return estadoEncontrado ? estadoEncontrado.municipios.map(m => m.nombre) : [];
    },

    contactosFiltrados() {
      const query = this.searchQuery.toLowerCase().trim();

      if (!query) {
        return this.contactos.slice(0, 50);
      }

      return this.contactos.filter(c =>
        c.nombreCompleto.toLowerCase().includes(query)
      ).slice(0, 10);
    },

    contactoSeleccionadoNombre() {
      const id = this.formData.contactoExistente;
      if (!id) return '';
      const contacto = this.contactos.find(c => c.id_cliente == id);
      return contacto ? contacto.nombreCompleto : '';
    }
  },
  methods: {
    updateField(campo, valor, tipoValidacion = null) {
      let valorLimpio = valor;

      if (tipoValidacion === 'texto') {
        valorLimpio = valor.replace(/[^a-zA-Z\sñÑáéíóúÁÉÍÓÚ.]/g, '');
      }
      else if (tipoValidacion === 'telefono') {
        valorLimpio = valor.replace(/\D/g, '').slice(0, 10);
      }
      this.$emit('update:formData', {
        ...this.formData,
        [campo]: valorLimpio
      });

      if (valor !== valorLimpio) {
        this.$forceUpdate();
      }
    },
    onCambioEstado(nuevoEstado) {
      this.$emit('update:formData', {
        ...this.formData,
        estado: nuevoEstado,
        localidad: ''
      });
    },
    async obtenerUsuarios() {
      try {
        const response = await clientService.getAllclients();
        this.contactos = response.data.map(c => ({
          ...c,
          nombreCompleto: `${c.nombre} ${c.apellidoPaterno || ''} ${c.apellidoMaterno || ''}`.trim()
        }));

        if (this.formData.contactoExistente) {
          const selected = this.contactos.find(c => c.id_cliente == this.formData.contactoExistente);
          if (selected) {
            this.searchQuery = selected.nombreCompleto;
          }
        }
      } catch (error) {
        console.error("Error al obtener contactos:", error);
      }
    },

    seleccionarContacto(c) {
      this.searchQuery = c.nombreCompleto; 
      this.showResults = false; 

      const updateMap = {
        contactoExistente: c.id_cliente,
        nombre: c.nombre,
        apellidoPaterno: c.apellidoPaterno,
        apellidoMaterno: c.apellidoMaterno,
        telefono: c.telefono,
        celular: c.celular,
        email: c.correo,
        estado: c.estado,
        localidad: c.localidad,
        notas: c.Notas,
        fuente: c.FuenteC,
      }

      this.$emit('update:formData', { ...this.formData, ...updateMap });
    },

    seleccionarNuevoContacto() {
      this.searchQuery = '';
      this.showResults = false;

      this.$emit('update:formData', {
        ...this.formData,
        contactoExistente: '',
        nombre: '',
        apellidoPaterno: '',
        apellidoMaterno: '',
        telefono: '',
        celular: '',
        email: '',
        estado: '',
        localidad: '',
        notas: '',
        fuente: '',
      });
    },

    hideResultsDelayed() {
      setTimeout(() => {
        this.showResults = false;
      }, 150);
    }
  },
  mounted() {
    this.obtenerUsuarios();

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};
</script>

<style scoped>
.paso-uno-container {
  padding: 0;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 25px;
}

.form-section-header {
  margin-bottom: 20px;
}

.form-section-header h3 {
  font-size: 20px;
  font-weight: 700;
  color: var(--color-secundario);
  margin-bottom: 8px;
}

.form-section-header p {
  font-size: 15px;
  color: var(--color-texto-secundario);
}

.header-icon {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-icon svg {
  color: var(--color-primario);
  width: 20px;
  height: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 8px;
  font-size: 14px;
  color: var(--color-texto-secundario);
  font-weight: 600;
}

.leyenda-obligatoria {
  font-size: 13px;
  color: var(--color-texto-secundario);
  margin-top: 8px;
  font-weight: 500;
}

.asterisco {
  color: #e74c3c;
  font-weight: bold;
  margin-right: 4px;
}

.form-group input,
.form-group select,
.form-group textarea {
  height: 42px;
  border: 1px solid var(--color-borde);
  border-radius: 8px;
  padding: 0 15px;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
  font-family: sans-serif;
}

.form-group textarea {
  height: auto;
  padding: 12px 15px;
  resize: vertical;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--color-primario);
  box-shadow: 0 0 0 3px rgba(47, 141, 183, 0.2);
}


.search-container {
  position: relative;
}

.input-search {
  height: 42px;
  border: 1px solid var(--color-borde);
  border-radius: 8px;
  padding: 0 15px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  padding-right: 40px;
}

.input-search.contact-loaded {
  background-color: var(--color-fondo-claro);
  font-weight: 600;
}

.clear-selection-button {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  cursor: pointer;
  color: #e74c3c;
  font-weight: bold;
  font-size: 16px;
  padding: 5px;
  line-height: 1;
  z-index: 10;
}

.clear-selection-button:hover {
  color: #c0392b;
}


.autocomplete-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 100;
  list-style: none;
  padding: 0;
  margin: 4px 0 0 0;
  border: 1px solid var(--color-borde);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 300px;
  overflow-y: auto;
}

.autocomplete-results li {
  padding: 10px 15px;
  cursor: pointer;
  font-size: 14px;
  color: var(--color-texto-principal);
  border-bottom: 1px solid #eee;
}

.autocomplete-results li:last-child {
  border-bottom: none;
}

.autocomplete-results li:hover {
  background-color: var(--color-fondo-claro);
  color: var(--color-primario);
}

.no-results-message {
  padding: 10px 15px;
  font-size: 14px;
  color: var(--color-texto-secundario);
  background-color: #f7f7f7;
  border-radius: 8px;
  margin-top: 4px;
}


.span-3 {
  grid-column: span 3;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .span-3 {
    grid-column: span 1;
  }

  .info-message {
    grid-column: span 1;
  }
}
</style>